<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div class="row justify-content-center">
      <div class="col-sm-12 col-xs-12">
        <div class="pb-0 col-sm-12 d-flex mainDiv">
          <div class="col-sm-12 col-xs-12">
            <div class="page-header">
              <a-button type="text" @click="goBack" class="back-button">
                <i class="fa-solid fa-arrow-left" />
                구독관리(v2)
              </a-button>
              <div class="header-actions">
                <a-button @click="navigateToEdit">
                  <i class="fa-regular fa-pen-to-square" />
                  수정
                </a-button>
                <a-popconfirm
                  title="이 구독을 삭제하시겠습니까?"
                  ok-text="삭제"
                  cancel-text="취소"
                  @confirm="handleDelete"
                >
                  <a-button danger>
                    <i class="fa-regular fa-trash-can" />
                    삭제
                  </a-button>
                </a-popconfirm>
              </div>
            </div>

            <a-spin :spinning="loading">
              <div v-if="subscription" class="detail-content">
                <div
                  class="hero-card"
                  :class="`status-${subscription.status?.toLowerCase()}`"
                >
                  <div class="hero-header">
                    <div class="status-badge">
                      <span class="status-dot"></span>
                      {{ getSubscriptionStatusText(subscription.status) }}
                    </div>
                    <h1 class="company-name">{{ subscription.companyName }}</h1>
                    <div class="package-info">
                      <span class="package-name">{{
                        subscription.packageName
                      }}</span>
                      <span class="price">
                        {{ formatPrice(subscription.finalUsageFee) }}
                        <span class="period">{{
                          getPaymentCycleText(subscription.paymentCycle)
                        }}</span>
                      </span>
                    </div>
                    <div v-if="subscription.discountType" class="discount-info">
                      <a-tag color="orange">
                        {{
                          subscription.discountType === "PERCENTAGE"
                            ? `${subscription.discountValue}% 할인`
                            : `${formatPrice(subscription.discountValue)} 할인`
                        }}
                      </a-tag>
                    </div>
                  </div>

                  <div class="hero-cards">
                    <div class="info-card">
                      <i class="fa-regular fa-calendar" />
                      <div class="info-content">
                        <div class="info-label">다음 결제</div>
                        <template v-if="subscription.paymentCycle === 'ONCE'">
                          <div class="info-value">
                            {{
                              subscription.completedRounds >= 1
                                ? "결제 완료"
                                : "미결제"
                            }}
                          </div>
                        </template>
                        <template v-else>
                          <div class="info-value">
                            {{ nextBillingDate || "-" }}
                          </div>
                          <div class="info-sub">
                            {{ (subscription.completedRounds || 0) + 1 }}회차
                          </div>
                        </template>
                      </div>
                    </div>

                    <div
                      class="info-card"
                      :class="{ 'card-warning': isCardExpired }"
                    >
                      <i class="fa-regular fa-credit-card" />
                      <div class="info-content">
                        <div class="info-label">결제 카드</div>
                        <template v-if="subscription.cardInfo">
                          <div class="info-value">
                            {{ subscription.cardInfo.cardNumber }}
                          </div>
                          <div class="info-sub">
                            {{ subscription.cardInfo.cardCompany }}
                            <a-tag
                              v-if="isCardExpired"
                              color="error"
                              size="small"
                              >만료</a-tag
                            >
                          </div>
                        </template>
                        <template v-else>
                          <div class="info-value text-muted">미등록</div>
                        </template>
                      </div>
                      <a-button type="link" size="small" @click="openCardModal">
                        {{ subscription.cardInfo ? "변경" : "등록" }}
                      </a-button>
                    </div>
                  </div>

                  <div class="hero-actions">
                    <a-button
                      type="primary"
                      size="large"
                      :disabled="!canProcessPayment"
                      class="primary-btn"
                      @click="openPaymentModal"
                    >
                      <i class="fa-solid fa-credit-card" />
                      결제 진행
                    </a-button>
                    <a-dropdown :trigger="['click']">
                      <a-button size="large">
                        상태 변경
                        <i
                          class="fa-solid fa-chevron-down"
                          style="margin-left: 8px"
                        />
                      </a-button>
                      <template #overlay>
                        <a-menu>
                          <a-menu-item
                            v-for="(
                              label, key
                            ) in ResellerSubscriptionStatusLabel"
                            :key="key"
                            :disabled="key === subscription.status"
                            @click="handleStatusChange(key)"
                          >
                            <a-tag
                              :color="ResellerSubscriptionStatusColor[key]"
                              size="small"
                            >
                              {{ label }}
                            </a-tag>
                          </a-menu-item>
                        </a-menu>
                      </template>
                    </a-dropdown>
                  </div>
                </div>

                <div class="section-card">
                  <h3 class="section-title">
                    <i class="fa-solid fa-history" />
                    결제 내역
                  </h3>
                  <a-table
                    :columns="paymentColumns"
                    :data-source="paymentHistory"
                    :loading="loadingPayments"
                    :pagination="false"
                    :row-key="(record) => record._id || record.id"
                    size="small"
                  >
                    <template #bodyCell="{ column, record }">
                      <template v-if="column.key === 'paymentRound'">
                        <a-tag v-if="record.paymentRound === 0" color="purple"
                          >설치비</a-tag
                        >
                        <span v-else>{{ record.paymentRound }}회차</span>
                      </template>
                      <template v-else-if="column.key === 'amount'">
                        <div class="amount-cell">
                          <template
                            v-if="
                              record.status === 'REFUNDED' ||
                              record.status === 'CANCELED'
                            "
                          >
                            <span class="payment-amount canceled-full">
                              {{
                                formatPrice(
                                  record.originalAmount || record.amount
                                )
                              }}
                            </span>
                          </template>
                          <template
                            v-else-if="record.status === 'PARTIAL_REFUNDED'"
                          >
                            <span class="payment-amount canceled-partial">
                              {{
                                formatPrice(
                                  record.originalAmount || record.amount
                                )
                              }}
                            </span>
                            <span class="payment-amount remaining">
                              {{ formatPrice(record.amount) }}
                            </span>
                          </template>
                          <template v-else>
                            {{ formatPrice(record.amount) }}
                          </template>
                        </div>
                      </template>
                      <template v-else-if="column.key === 'paidAt'">
                        {{ formatDateTime(record.paidAt) }}
                      </template>
                      <template v-else-if="column.key === 'status'">
                        <a-tag :color="PaymentStatusColor[record.status]">
                          {{ PaymentStatusLabel[record.status] }}
                        </a-tag>
                      </template>
                      <template v-else-if="column.key === 'receipt'">
                        <a
                          v-if="record.receiptUrl"
                          :href="record.receiptUrl"
                          target="_blank"
                          >영수증</a
                        >
                        <span v-else>-</span>
                      </template>
                      <template v-else-if="column.key === 'action'">
                        <a-button
                          v-if="
                            record.status === 'COMPLETED' ||
                            record.status === 'PARTIAL_REFUNDED'
                          "
                          type="link"
                          danger
                          size="small"
                          @click="openCancelModal(record)"
                        >
                          취소
                        </a-button>
                        <span v-else class="text-muted">-</span>
                      </template>
                    </template>
                  </a-table>
                  <div
                    v-if="!loadingPayments && paymentHistory.length === 0"
                    class="empty-payments"
                  >
                    <a-empty description="결제 내역이 없습니다" />
                  </div>
                </div>

                <div class="info-grid">
                  <div class="section-card">
                    <h3 class="section-title">
                      <i class="fa-solid fa-file-contract" />
                      계약 정보
                    </h3>
                    <div class="info-list">
                      <div class="info-row">
                        <span class="label">결제 주기</span>
                        <span class="value">
                          <a-tag
                            :color="
                              PaymentCycleColor[subscription.paymentCycle]
                            "
                          >
                            {{ PaymentCycleLabel[subscription.paymentCycle] }}
                          </a-tag>
                        </span>
                      </div>
                      <div
                        v-if="subscription.paymentCycle !== 'ONCE'"
                        class="info-row"
                      >
                        <span class="label">서비스 시작일</span>
                        <span class="value">{{
                          formatDate(subscription.startDate) || "-"
                        }}</span>
                      </div>
                      <div
                        v-if="subscription.paymentCycle !== 'ONCE'"
                        class="info-row"
                      >
                        <span class="label">계약 종료일</span>
                        <span class="value">{{
                          formatDate(subscription.endDate) || "무제한"
                        }}</span>
                      </div>
                      <div
                        v-if="subscription.paymentCycle === 'ONCE'"
                        class="info-row"
                      >
                        <span class="label">계약일</span>
                        <span class="value">{{
                          formatDate(subscription.contractDate) || "-"
                        }}</span>
                      </div>
                      <div class="info-row">
                        <span class="label">결제 회차</span>
                        <span class="value"
                          >{{ subscription.completedRounds || 0 }}회 완료</span
                        >
                      </div>
                      <div
                        v-if="subscription.finalInstallationFee > 0"
                        class="info-row"
                      >
                        <span class="label">설치비</span>
                        <span class="value">
                          {{ formatPrice(subscription.finalInstallationFee) }}
                          <a-tag
                            v-if="subscription.installationFeePaid"
                            color="green"
                            size="small"
                            >결제완료</a-tag
                          >
                          <a-tag v-else color="orange" size="small"
                            >미결제</a-tag
                          >
                        </span>
                      </div>
                      <div v-if="subscription.businessNumber" class="info-row">
                        <span class="label">사업자번호</span>
                        <span class="value">{{
                          formatBusinessNumber(subscription.businessNumber)
                        }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="section-card">
                    <h3 class="section-title">
                      <i class="fa-solid fa-sticky-note" />
                      메모
                    </h3>
                    <div v-if="subscription.notes" class="notes-content">
                      {{ subscription.notes }}
                    </div>
                    <div v-else class="empty-notes">
                      <span class="text-muted">등록된 메모가 없습니다</span>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else-if="!loading" class="not-found">
                <a-result
                  status="404"
                  title="구독을 찾을 수 없습니다"
                  sub-title="요청하신 구독 정보가 존재하지 않습니다."
                >
                  <template #extra>
                    <a-button type="primary" @click="goBack">목록으로</a-button>
                  </template>
                </a-result>
              </div>
            </a-spin>
          </div>
        </div>
      </div>
    </div>

    <CardManagementModal
      v-if="subscription"
      v-model:open="cardModalVisible"
      :company-id="subscription.companyId"
      :current-card-id="subscription.cardId"
      @select="handleCardSelect"
    />

    <!-- 결제 확인 모달 -->
    <a-modal
      v-model:open="paymentModalVisible"
      title="결제 확인"
      :confirm-loading="isProcessingPayment"
      @ok="processPayment"
      @cancel="paymentModalVisible = false"
      ok-text="결제 진행"
      cancel-text="취소"
    >
      <a-spin :spinning="isLoadingPaymentStatus">
        <div v-if="subscription && paymentStatus" class="payment-confirm">
          <a-descriptions :column="1" bordered size="small">
            <a-descriptions-item label="회사명">
              {{ subscription.companyName }}
            </a-descriptions-item>
            <a-descriptions-item label="패키지명">
              {{ subscription.packageName }}
            </a-descriptions-item>
            <a-descriptions-item label="결제 회차">
              {{ paymentStatus.completedRounds }}회 완료
              <template v-if="paymentStatus.totalRounds">
                / 총 {{ paymentStatus.totalRounds }}회
              </template>
              <template v-else> (무제한) </template>
            </a-descriptions-item>
            <a-descriptions-item label="이번 결제금액">
              <span
                v-if="paymentStatus.isFreeTrial"
                class="payment-amount free-trial"
              >
                0원 (무료체험)
              </span>
              <span
                v-else-if="paymentStatus.nextPaymentAmount !== null"
                class="payment-amount"
              >
                {{ formatPrice(paymentStatus.nextPaymentAmount) }}
              </span>
              <span v-else class="payment-amount">
                {{ formatPrice(subscription.finalUsageFee) }}
              </span>
              <span
                v-if="
                  paymentStatus.nextPaymentIsProRata ||
                  paymentStatus.isFreeTrial
                "
                class="pro-rata-badge"
              >
                <a-tag
                  :color="paymentStatus.isFreeTrial ? 'green' : 'blue'"
                  size="small"
                >
                  {{ paymentStatus.isFreeTrial ? "무료체험" : "일할계산" }}
                </a-tag>
              </span>
            </a-descriptions-item>
            <a-descriptions-item
              v-if="paymentStatus.nextPaymentDescription"
              label="청구기간"
            >
              {{ paymentStatus.nextPaymentDescription }}
            </a-descriptions-item>
          </a-descriptions>
          <a-alert
            type="warning"
            show-icon
            style="margin-top: 16px"
            message="등록된 결제카드로 즉시 결제가 진행됩니다."
          />
        </div>
      </a-spin>
    </a-modal>

    <!-- 결제 취소 모달 -->
    <a-modal
      v-model:open="cancelModalVisible"
      title="결제 취소"
      :confirm-loading="cancelLoading"
      @ok="handleCancelPayment"
      @cancel="closeCancelModal"
      ok-text="취소 진행"
      cancel-text="닫기"
      ok-type="danger"
    >
      <div v-if="cancelTarget" class="cancel-modal-content">
        <div class="cancel-info-card">
          <div class="info-row">
            <span class="label">회사명</span>
            <span class="value">{{ subscription?.companyName }}</span>
          </div>
          <div class="info-row">
            <span class="label">패키지</span>
            <span class="value">{{ subscription?.packageName }}</span>
          </div>
          <div class="info-row">
            <span class="label">결제금액</span>
            <span class="value amount">{{
              formatPrice(cancelTarget.amount)
            }}</span>
          </div>
          <div v-if="cancelTarget.canceledAmount" class="info-row">
            <span class="label">기취소금액</span>
            <span class="value canceled">{{
              formatPrice(cancelTarget.canceledAmount)
            }}</span>
          </div>
          <div class="info-row highlight">
            <span class="label">취소가능금액</span>
            <span class="value">{{ formatPrice(maxCancelAmount) }}</span>
          </div>
        </div>

        <a-divider />

        <a-form layout="vertical">
          <a-form-item label="취소 유형" required>
            <a-radio-group v-model:value="cancelForm.cancelType">
              <a-radio value="full">전액 취소</a-radio>
              <a-radio value="partial">부분 취소</a-radio>
            </a-radio-group>
          </a-form-item>
          <a-form-item
            v-if="cancelForm.cancelType === 'partial'"
            label="취소 금액"
            required
          >
            <a-input-number
              v-model:value="cancelForm.cancelAmount"
              :min="1"
              :max="maxCancelAmount"
              :formatter="
                (value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              "
              :parser="(value) => Number(value.replace(/,/g, ''))"
              style="width: 200px"
            />
            <span class="unit">원</span>
          </a-form-item>
          <a-form-item label="취소 사유" required>
            <a-select
              v-model:value="cancelForm.cancelReasonType"
              placeholder="취소 사유를 선택하세요"
              style="width: 100%"
              @change="handleCancelReasonTypeChange"
            >
              <a-select-option value="고객 요청">고객 요청</a-select-option>
              <a-select-option value="단순 변심">단순 변심</a-select-option>
              <a-select-option value="서비스 불만족"
                >서비스 불만족</a-select-option
              >
              <a-select-option value="중복 결제">중복 결제</a-select-option>
              <a-select-option value="오결제">오결제</a-select-option>
              <a-select-option value="계약 해지">계약 해지</a-select-option>
              <a-select-option value="기타">기타 (직접입력)</a-select-option>
            </a-select>
          </a-form-item>
          <a-form-item
            v-if="cancelForm.cancelReasonType === '기타'"
            label="상세 사유"
            required
          >
            <a-input
              v-model:value="cancelForm.cancelReasonCustom"
              placeholder="취소 사유를 직접 입력하세요"
            />
          </a-form-item>
        </a-form>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { message, Modal } from "ant-design-vue";
import dayjs from "dayjs";
import { resellerSubscriptionApi, resellerPaymentHistoryApi } from "./api";
import {
  ResellerSubscriptionStatusLabel,
  ResellerSubscriptionStatusColor,
  PaymentCycleLabel,
  PaymentCycleColor,
  PaymentStatusLabel,
  PaymentStatusColor,
} from "./enums";
import {
  formatPrice,
  formatDate,
  formatDateTime,
  formatBusinessNumber,
} from "./utils/formatters";
import {
  getSubscriptionStatusText,
  getPaymentCycleText,
} from "./utils/statusHelpers";
import { isCardExpired as checkCardExpired } from "./utils/cardExpiry";
import CardManagementModal from "./components/CardManagementModal.vue";

export default {
  name: "SubscriptionDetailV2",
  components: { CardManagementModal },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const subscriptionId = computed(() => route.params.id);

    const loading = ref(false);
    const subscription = ref(null);
    const paymentHistory = ref([]);
    const loadingPayments = ref(false);
    const cardModalVisible = ref(false);
    const paymentModalVisible = ref(false);
    const paymentStatus = ref(null);
    const isLoadingPaymentStatus = ref(false);
    const isProcessingPayment = ref(false);

    // 결제 취소 관련
    const cancelModalVisible = ref(false);
    const cancelLoading = ref(false);
    const cancelTarget = ref(null);
    const cancelForm = ref({
      cancelType: "full",
      cancelAmount: 0,
      cancelReasonType: "",
      cancelReasonCustom: "",
    });

    const paymentColumns = [
      {
        title: "구분",
        key: "paymentRound",
        dataIndex: "paymentRound",
        width: 80,
      },
      {
        title: "결제금액",
        key: "amount",
        dataIndex: "amount",
        width: 120,
        align: "right",
      },
      { title: "결제일시", key: "paidAt", dataIndex: "paidAt", width: 150 },
      {
        title: "상태",
        key: "status",
        dataIndex: "status",
        width: 80,
        align: "center",
      },
      { title: "영수증", key: "receipt", width: 80, align: "center" },
      { title: "관리", key: "action", width: 70, align: "center" },
    ];

    const isCardExpired = computed(() => {
      if (!subscription.value?.cardInfo?.cardExpiry) return false;
      return checkCardExpired(subscription.value.cardInfo.cardExpiry);
    });

    const canProcessPayment = computed(() => {
      if (!subscription.value) return false;
      return (
        subscription.value.status === "ACTIVE" &&
        subscription.value.cardId &&
        !isCardExpired.value
      );
    });

    const nextBillingDate = computed(() => {
      if (!subscription.value?.paymentDay || !subscription.value?.startDate)
        return "";
      const completedRounds = subscription.value.completedRounds || 0;
      const startDate = dayjs(subscription.value.startDate);
      const nextBilling = startDate
        .add(completedRounds + 1, "month")
        .date(subscription.value.paymentDay);
      return nextBilling.format("YYYY-MM-DD");
    });

    const fetchSubscription = async () => {
      loading.value = true;
      try {
        const response = await resellerSubscriptionApi.getDetail(
          subscriptionId.value
        );
        subscription.value = response;
      } catch (error) {
        console.error("구독 조회 실패:", error);
        message.error("구독 정보를 불러오는데 실패했습니다.");
      } finally {
        loading.value = false;
      }
    };

    const fetchPaymentHistory = async () => {
      loadingPayments.value = true;
      try {
        const response = await resellerPaymentHistoryApi.getBySubscription(
          subscriptionId.value
        );
        paymentHistory.value = response || [];
      } catch (error) {
        console.error("결제 내역 조회 실패:", error);
        paymentHistory.value = [];
      } finally {
        loadingPayments.value = false;
      }
    };

    const handleStatusChange = async (newStatus) => {
      if (newStatus === "TERMINATED" || newStatus === "SUSPENDED") {
        Modal.confirm({
          title: "상태 변경 확인",
          content:
            newStatus === "TERMINATED"
              ? "종료 상태로 변경하면 되돌릴 수 없습니다. 계속하시겠습니까?"
              : "중단 상태로 변경하시겠습니까?",
          okText: "확인",
          cancelText: "취소",
          async onOk() {
            await updateStatus(newStatus);
          },
        });
      } else {
        await updateStatus(newStatus);
      }
    };

    const updateStatus = async (newStatus) => {
      try {
        await resellerSubscriptionApi.changeStatus(
          subscriptionId.value,
          newStatus
        );
        message.success("상태가 변경되었습니다.");
        fetchSubscription();
      } catch (error) {
        console.error("상태 변경 실패:", error);
        message.error("상태 변경에 실패했습니다.");
      }
    };

    // 결제 현황 조회
    const fetchPaymentStatus = async () => {
      try {
        isLoadingPaymentStatus.value = true;
        const response = await resellerSubscriptionApi.getPaymentStatus(
          subscriptionId.value
        );
        paymentStatus.value = response;
      } catch (error) {
        console.error("결제 현황 조회 실패:", error);
        paymentStatus.value = null;
      } finally {
        isLoadingPaymentStatus.value = false;
      }
    };

    // 결제 모달 열기
    const openPaymentModal = async () => {
      paymentModalVisible.value = true;
      await fetchPaymentStatus();
    };

    // 결제 처리
    const processPayment = async () => {
      if (!subscription.value) return;

      try {
        isProcessingPayment.value = true;
        const result = await resellerSubscriptionApi.processPayment(
          subscriptionId.value
        );

        if (result.isFreeTrial) {
          message.info(
            result.message || "무료체험 기간으로 결제가 스킵되었습니다."
          );
        } else if (result.amount && result.amount > 0) {
          message.success(
            `${
              result.paymentRound
            }회차 결제가 완료되었습니다. (${result.amount.toLocaleString()}원)`
          );
        } else {
          message.success(result.message || "결제가 완료되었습니다.");
        }

        paymentModalVisible.value = false;
        fetchSubscription();
        fetchPaymentHistory();
      } catch (error) {
        console.error("결제 실패:", error);
        message.error(error.response?.data?.message || "결제에 실패했습니다.");
      } finally {
        isProcessingPayment.value = false;
      }
    };

    // 취소 가능 금액 계산
    const maxCancelAmount = computed(() => {
      if (!cancelTarget.value) return 0;
      return (
        cancelTarget.value.amount - (cancelTarget.value.canceledAmount || 0)
      );
    });

    // 취소 모달 열기
    const openCancelModal = (record) => {
      cancelTarget.value = record;
      cancelForm.value.cancelType = "full";
      cancelForm.value.cancelAmount =
        record.amount - (record.canceledAmount || 0);
      cancelForm.value.cancelReasonType = "";
      cancelForm.value.cancelReasonCustom = "";
      cancelModalVisible.value = true;
    };

    // 취소 사유 타입 변경 핸들러
    const handleCancelReasonTypeChange = () => {
      if (cancelForm.value.cancelReasonType !== "기타") {
        cancelForm.value.cancelReasonCustom = "";
      }
    };

    // 취소 모달 닫기
    const closeCancelModal = () => {
      cancelModalVisible.value = false;
      cancelTarget.value = null;
    };

    // 결제 취소 처리
    const handleCancelPayment = async () => {
      if (!cancelTarget.value) return;

      if (!cancelForm.value.cancelReasonType) {
        message.warning("취소 사유를 선택해주세요.");
        return;
      }

      if (
        cancelForm.value.cancelReasonType === "기타" &&
        !cancelForm.value.cancelReasonCustom.trim()
      ) {
        message.warning("취소 사유를 직접 입력해주세요.");
        return;
      }

      if (cancelForm.value.cancelType === "partial") {
        if (
          !cancelForm.value.cancelAmount ||
          cancelForm.value.cancelAmount <= 0
        ) {
          message.warning("취소 금액을 입력해주세요.");
          return;
        }
        if (cancelForm.value.cancelAmount > maxCancelAmount.value) {
          message.warning("취소 가능 금액을 초과했습니다.");
          return;
        }
      }

      const cancelAmount =
        cancelForm.value.cancelType === "full"
          ? undefined
          : cancelForm.value.cancelAmount;

      const cancelReason =
        cancelForm.value.cancelReasonType === "기타"
          ? cancelForm.value.cancelReasonCustom.trim()
          : cancelForm.value.cancelReasonType;

      Modal.confirm({
        title: "결제 취소 확인",
        content: `${
          cancelForm.value.cancelType === "full"
            ? "전액"
            : formatPrice(cancelForm.value.cancelAmount)
        }을(를) 취소하시겠습니까?`,
        okText: "취소 진행",
        okType: "danger",
        cancelText: "닫기",
        async onOk() {
          try {
            cancelLoading.value = true;
            const paymentId = cancelTarget.value._id || cancelTarget.value.id;
            if (!paymentId) {
              message.error("결제 ID를 찾을 수 없습니다.");
              return;
            }
            await resellerPaymentHistoryApi.cancelPayment(
              paymentId,
              cancelReason,
              cancelAmount
            );

            message.success("결제가 취소되었습니다.");
            closeCancelModal();
            fetchSubscription();
            fetchPaymentHistory();
          } catch (error) {
            console.error("결제 취소 실패:", error);
            const errorMessage =
              error.response?.data?.message || "결제 취소에 실패했습니다.";
            message.error(errorMessage);
          } finally {
            cancelLoading.value = false;
          }
        },
      });
    };

    const handleDelete = async () => {
      try {
        await resellerSubscriptionApi.delete(subscriptionId.value);
        message.success("구독이 삭제되었습니다.");
        goBack();
      } catch (error) {
        console.error("구독 삭제 실패:", error);
        message.error("구독 삭제에 실패했습니다.");
      }
    };

    const openCardModal = () => {
      cardModalVisible.value = true;
    };

    const handleCardSelect = async (cardId) => {
      try {
        await resellerSubscriptionApi.update(subscriptionId.value, { cardId });
        message.success("결제 카드가 변경되었습니다.");
        cardModalVisible.value = false;
        fetchSubscription();
      } catch (error) {
        console.error("카드 변경 실패:", error);
        message.error("카드 변경에 실패했습니다.");
      }
    };

    const goBack = () => {
      router.push("/subscriptions-v2/list");
    };

    const navigateToEdit = () => {
      router.push(`/subscriptions-v2/${subscriptionId.value}/edit`);
    };

    onMounted(() => {
      fetchSubscription();
      fetchPaymentHistory();
    });

    return {
      loading,
      subscription,
      paymentHistory,
      loadingPayments,
      cardModalVisible,
      paymentColumns,
      isCardExpired,
      canProcessPayment,
      nextBillingDate,
      ResellerSubscriptionStatusLabel,
      ResellerSubscriptionStatusColor,
      PaymentCycleLabel,
      PaymentCycleColor,
      PaymentStatusLabel,
      PaymentStatusColor,
      formatPrice,
      formatDate,
      formatDateTime,
      formatBusinessNumber,
      getSubscriptionStatusText,
      getPaymentCycleText,
      handleStatusChange,
      handleDelete,
      openCardModal,
      handleCardSelect,
      goBack,
      navigateToEdit,
      // 결제 모달
      paymentModalVisible,
      paymentStatus,
      isLoadingPaymentStatus,
      isProcessingPayment,
      openPaymentModal,
      processPayment,
      // 결제 취소 모달
      cancelModalVisible,
      cancelLoading,
      cancelTarget,
      cancelForm,
      maxCancelAmount,
      openCancelModal,
      closeCancelModal,
      handleCancelPayment,
      handleCancelReasonTypeChange,
    };
  },
};
</script>

<style lang="scss" scoped>
.row {
  padding: 28px 28px 72px 28px !important;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.back-button {
  padding: 0;
  color: #666;
  font-size: 14px;

  i {
    margin-right: 8px;
  }

  &:hover {
    color: #1890ff;
  }
}

.header-actions {
  display: flex;
  gap: 8px;

  i {
    margin-right: 6px;
  }
}

.hero-card {
  background: #fff;
  border-radius: 16px;
  padding: 28px;
  margin-bottom: 24px;
  border-left: 4px solid #6b7280;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  &.status-active {
    border-left-color: #22c55e;
  }
  &.status-contract {
    border-left-color: #3b82f6;
  }
  &.status-setup {
    border-left-color: #06b6d4;
  }
  &.status-inspection {
    border-left-color: #8b5cf6;
  }
  &.status-suspended {
    border-left-color: #f59e0b;
  }
  &.status-terminated {
    border-left-color: #ef4444;
  }
}

.hero-header {
  margin-bottom: 24px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  background: #f3f4f6;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 8px;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.company-name {
  font-size: 26px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px;
}

.package-info {
  display: flex;
  align-items: baseline;
  gap: 16px;
}

.package-name {
  font-size: 15px;
  color: #6b7280;
}

.price {
  font-size: 22px;
  font-weight: 600;
  color: #111827;
}

.period {
  font-size: 14px;
  font-weight: 400;
  color: #9ca3af;
  margin-left: 4px;
}

.discount-info {
  margin-top: 8px;
}

.hero-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

.info-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  position: relative;

  > i {
    font-size: 20px;
    color: #6b7280;
    margin-top: 2px;
  }

  &.card-warning {
    background: #fef2f2;
    border-color: #fecaca;

    > i {
      color: #ef4444;
    }
  }

  > button {
    position: absolute;
    top: 12px;
    right: 12px;
  }
}

.info-content {
  flex: 1;
}

.info-label {
  font-size: 12px;
  color: #9ca3af;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.info-value {
  font-size: 16px;
  font-weight: 600;
  color: #111827;

  &.text-muted {
    color: #9ca3af;
  }
}

.info-sub {
  font-size: 13px;
  color: #6b7280;
  margin-top: 2px;
}

.hero-actions {
  display: flex;
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid #f3f4f6;
}

.primary-btn {
  background: #fff !important;
  border: 1px solid #d1d5db !important;
  color: #374151 !important;
  font-weight: 500 !important;

  i {
    margin-right: 8px;
  }

  &:hover:not(:disabled) {
    background: #f9fafb !important;
    border-color: #9ca3af !important;
  }

  &:disabled {
    background: #f3f4f6 !important;
    border-color: #e5e7eb !important;
    color: #9ca3af !important;
    box-shadow: none !important;
    cursor: not-allowed;
    opacity: 0.7;
  }
}

.section-card {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #17191c;
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;

  i {
    color: #6b7280;
  }
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .label {
    font-size: 14px;
    color: #666;
  }

  .value {
    font-size: 14px;
    font-weight: 500;
    color: #17191c;
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.notes-content {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  white-space: pre-wrap;
}

.empty-notes,
.empty-payments {
  padding: 24px 0;
  text-align: center;
}

.text-muted {
  color: #9ca3af;
  font-size: 14px;
}

.not-found {
  padding: 48px 0;
}

/* ============================================ */
/* 결제 확인 모달 */
/* ============================================ */
.payment-confirm {
  padding: 8px 0;
}

.payment-confirm :deep(.ant-descriptions-item-label) {
  background: #fafafa;
  font-weight: 500;
}

.payment-confirm .payment-amount {
  font-size: 20px;
  font-weight: 700;
  color: #1890ff;
}

.payment-confirm .payment-amount.free-trial {
  color: #52c41a;
}

.pro-rata-badge {
  margin-left: 8px;
}

/* 금액 셀 */
.amount-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.payment-amount.canceled-full {
  color: #ff4d4f;
  text-decoration: line-through;
}

.payment-amount.canceled-partial {
  color: #1a1a2e;
  text-decoration: line-through;
  font-size: 12px;
  font-weight: 400;
}

.payment-amount.remaining {
  color: #1890ff;
  font-weight: 700;
}

/* ============================================ */
/* 취소 모달 */
/* ============================================ */
.cancel-modal-content {
  padding: 8px 0;
}

.cancel-info-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
}

.cancel-info-card .info-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.cancel-info-card .info-row:last-child {
  border-bottom: none;
}

.cancel-info-card .info-row .label {
  color: #666;
  font-size: 13px;
}

.cancel-info-card .info-row .value {
  font-weight: 500;
  color: #1a1a2e;
}

.cancel-info-card .info-row .value.amount {
  color: #1890ff;
}

.cancel-info-card .info-row .value.canceled {
  color: #ff4d4f;
}

.cancel-info-card .info-row.highlight {
  background: #e6f7ff;
  margin: 8px -16px -16px;
  padding: 12px 16px;
  border-radius: 0 0 8px 8px;
}

.cancel-info-card .info-row.highlight .value {
  color: #52c41a;
  font-weight: 600;
  font-size: 16px;
}

.unit {
  margin-left: 8px;
  color: #666;
}
</style>
